import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Grid2,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MdOutlineEdit, MdDelete } from "react-icons/md";
import { GiCheckMark } from "react-icons/gi";
import { IoIosSearch, IoMdClose } from "react-icons/io";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import AddIcon from "@mui/icons-material/Add";
import {
  useGetAllSettingsQuery,
  useAddSettingsMutation,
  useUpdateSettingsMutation,
  useDeleteSettingsMutation,
} from "store/slices/apis/projectSettingsSlice";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { usePermission } from "hooks/usePermission";
import { PROJECT_SETTING_CREATE, PROJECT_SETTING_EDIT, PROJECT_SETTING_DELETE } from "config";
import { ReactComponent as EditIcon } from "assets/PwC_Funct_Icons_Edit_Outline_Black_RGB.svg"
import {ReactComponent as DeleteIcon} from "assets/PwC_Funct_Icons_Delete_Outline_Black_RGB.svg"
import {ReactComponent as AddNewIcon} from "assets/PwC_Funct_Icons_Plus_Outline_Black_RGB.svg"
import {ReactComponent as SearchIcon} from "assets/PwC_Funct_Icons_Search_Outline_Black_RGB.svg"
import {ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"
import {ReactComponent as ChevronDownIcon} from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"
import {ReactComponent as CheckIcon} from "assets/PwC_Funct_Icons_Confirmed_Outline_Black_RGB.svg"

const AllSettings = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const addAccess = usePermission(PROJECT_SETTING_CREATE);
  const editAccess = usePermission(PROJECT_SETTING_EDIT);
  const deleteAccess = usePermission(PROJECT_SETTING_DELETE)

  const [addSettings] = useAddSettingsMutation();
  const [updateSettings] = useUpdateSettingsMutation();
  const [deleteSettings] = useDeleteSettingsMutation();
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [editMap, setEditMap] = useState({});
  const [addRow, setAddRow] = useState({});
  const [categoryEditMap, setCategoryEditMap] = useState({});
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryValue, setNewCategoryValue] = useState("");
  const [addingNewCategory, setAddingNewCategory] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [valueError, setValueError] = useState(false);
  const [isCategoryDelete, setIsCategoryDelete] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const { data: allSettings, refetch, isLoading } = useGetAllSettingsQuery({
    ProjectId: projectId,
    name: searchQuery?.trim(),
  });

  useEffect(() => {
    if (allSettings?.data) {
      setData(allSettings?.data);
    }
  }, [allSettings]);

  const groupedData = data.reduce((acc, curr) => {
    acc[curr.name] = curr.items;
    return acc;
  }, {});

  const clearSearch = () => setSearchQuery("");
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleAddNewCategory = () => {
    const category = newCategory.trim();
    const value = newCategoryValue.trim();

    setCategoryError(!category);
    setValueError(!value);
    if (!category || !value) return;

    addSettings({ body: { name: category, value, projectId } })
      .unwrap()
      .then((res) => {
        openNotification(
          res?.message || "Setting added successfully",
          "success"
        );
      })
      .catch(() => {
        openNotification("Failed to add setting", "error");
      });
    setNewCategory("");
    setNewCategoryValue("");
    setAddingNewCategory(false);
    setCategoryError(false);
    setValueError(false);
    refetch();
  };
  const handleEditChange = (id, value) => {
    setEditMap((prev) => ({
      ...prev,
      [id]: { ...prev[id], value },
    }));
  };

  const saveEdit = (id) => {
    const updated = editMap[id];
    if (!updated?.value?.trim()) return;

    updateSettings({
      id,
      body: { ...updated, projectId },
    })
      .unwrap()
      .then((res) => {
        openNotification(
          res?.message || "Setting updated successfully",
          "success"
        );
      })
      .catch(() => {
        openNotification("Failed to update setting", "error");
      });

    setEditMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    refetch();
  };

  const handleDelete = (id) => {
    setOpenDelete(true);
    setDeleteId(id);
    setIsCategoryDelete(false);
  };

  const confirmDelete = () => {
    if (isCategoryDelete) {
      categoryToDelete.forEach((id) => {
        deleteSettings({ id })
          .unwrap()
          .then((res) => {
            openNotification(res?.message || "Deleted successfully", "success");
          })
          .catch(() => {
            openNotification("Failed to delete item", "error");
          });
      });
    } else {
      deleteSettings({ id: deleteId })
        .unwrap()
        .then((res) => {
          openNotification(res?.message || "Deleted successfully", "success");
        })
        .catch(() => {
          openNotification("Failed to delete setting", "error");
        });
    }
    setOpenDelete(false);
    refetch();
  };

  const handleAddValue = (category) => {
    const value = addRow[category]?.trim();
    if (!value) return;

    addSettings({
      body: { name: category, value, projectId },
    })
      .unwrap()
      .then((res) => {
        openNotification(res?.message || "Value added successfully", "success");
        setAddRow((prev) => ({ ...prev, [category]: "" }));
      })
      .catch(() => {
        openNotification("Failed to add value", "error");
      });
    refetch();
  };

  const startEditCategory = (category) => {
    resetAllEdits();
    setCategoryEditMap((prev) => ({
      ...prev,
      [category]: { editing: true, temp: category },
    }));
  };

  const updateTempCategory = (category, value) => {
    setCategoryEditMap((prev) => ({
      ...prev,
      [category]: { ...prev[category], temp: value },
    }));
  };

  const cancelEditCategory = (category) => {
    setCategoryEditMap((prev) => {
      const copy = { ...prev };
      delete copy[category];
      return copy;
    });
  };

  const handleEditCategory = (oldCategory, newCategory) => {
    if (!newCategory?.trim()) return;

    const updates = groupedData[oldCategory]?.map((item) => ({
      ...item,
      name: newCategory,
    }));

    updates?.map((row) => {
      updateSettings({
        id: row.id,
        body: { ...row, name: newCategory, projectId },
      })
        .unwrap()
        .then((res) => {
          openNotification(res?.message || "Category updated", "success");
        })
        .catch(() => {
          openNotification("Failed to update category", "error");
        });
    });
    cancelEditCategory(oldCategory);
    refetch();
  };

  const handleDeleteCategory = (items) => {
    const ids = items.map((item) => item.id);
    setOpenDelete(true);
    setCategoryToDelete(ids);
    setIsCategoryDelete(true);
  };

  const resetAllEdits = () => {
    setCategoryEditMap({});
    setEditMap({});
    setAddingNewCategory(false);
    setNewCategory("");
    setNewCategoryValue("");
  };

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : null);
  };

  return (
    <>
      <Grid2 container spacing={2} sx={{ mt: "20px" }}>
        <Grid2 size={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: 'start', sm: 'center' },
              justifyContent: "space-between",
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                // flexGrow: 2,
                alignItems: { xs: "stretch", sm: "center" }, // Full width on mobile
                width: { xs: '100%', sm: 'auto' }, // Stack takes full width
                "& .MuiStack-root": {
                  paddingTop: "15px",
                },
              }}
            >
              <TextField
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Category Name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                       {/* <IoIosSearch /> */}
                      <SearchIcon style={{ height: "20px", width: "20px", fill:"#808080 " }} />
                    </InputAdornment>
                  ),
                  style: {
                    fontSize: "16px",
                  },
                  endAdornment: searchQuery?.length > 0 && (
                    <IconButton onClick={clearSearch} edge="end">
                      {/* <ClearIcon style={{ fontSize: "16px" }} /> */}
                      <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px" }} />
                    </IconButton>
                  ),
                }}
                sx={{
                  minWidth: { xs: '100%', sm: "510px" },
                  height: "48px",
                  maxWidth: "500px",
                  background: "#fff",
                  "& .MuiInputBase-root": {
                    height: "48px",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "16px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#696969",
                    opacity: 1,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "16px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#696969",
                    opacity: 1,
                  },
                }}
              />
            </Stack>
            {addAccess &&
              <Button
                variant="contained"
                // startIcon={<AddIcon />}
                startIcon={<AddNewIcon style={{ height: "20px", width: "20px", fill: "#FFFFFF" }}  />}
                onClick={() => {
                  resetAllEdits();
                  setAddingNewCategory(true);
                }}
                sx={{
                  backgroundColor: `${theme?.customSettings?.themeColor}`,
                  color: "#FFF",
                  padding: "5px 20px",
                  fontWeight: "500",
                  textTransform: "none",
                  fontSize: "16px",
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  // marginRight: "20px",
                  mt: '15px',
                  "&.Mui-disabled": {
                    border: "none",
                  },
                }}
              >
                Add New Setting
              </Button>
            }
          </Box>
        </Grid2>

        {addingNewCategory && (
          <Grid2
            container
            spacing={2}
          // sx={{
          //   alignItems: "flex-start",
          //   width: "100%",
          // }}
          >
            {/* Category Name */}
            <Grid2 size={{ xs: 6, sm: 5 }}>
              <TextField
                fullWidth
                size="small"
                label="Category Name"
                value={newCategory}
                error={categoryError}
                helperText={categoryError ? "Category name is required" : ""}
                onChange={(e) => {
                  setNewCategory(e.target.value);
                  setCategoryError(false);
                }}
                sx={{
                  // minWidth: '100%',
                  background: "white",
                  "& .MuiFormHelperText-root": {
                    background: "#f3f3f3",
                    margin: 0,
                    minHeight: "20px",
                  },
                }}
              />
            </Grid2>

            {/* Category Value */}
            <Grid2 size={{ xs: 6, sm: 5 }}>
              <TextField
                fullWidth
                size="small"
                label="Value"
                value={newCategoryValue}
                error={valueError}
                helperText={valueError ? "Value is required" : ""}
                onChange={(e) => {
                  setNewCategoryValue(e.target.value);
                  setValueError(false);
                }}
                sx={{
                  background: "white",
                  "& .MuiFormHelperText-root": {
                    background: "#f3f3f3",
                    margin: 0,
                    minHeight: "20px",
                  },
                }}
              />
            </Grid2>

            {/* Action Buttons */}
            <Grid2 xs={12} sm={2} sx={{ display: "flex", gap: 1, justifyContent: "flex-start" }}>
              <IconButton
                onClick={handleAddNewCategory}
                color="success"
                size="small"
                sx={{ marginTop: { xs: 0, sm: "5px" } }}
              >
                 {/* <GiCheckMark /> */}
                <CheckIcon style={{ fontSize: "30px", width:"30px", height:"30px", fill:"#32cd32" }} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setAddingNewCategory(false);
                  setNewCategory("");
                  setNewCategoryValue("");
                  setCategoryError(false);
                  setValueError(false);
                }}
                color="error"
                size="small"
                sx={{ marginTop: { xs: 0, sm: "5px" } }}
              >
                <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px" }} />
              </IconButton>
            </Grid2>
          </Grid2>

        )}
      </Grid2>
      <Grid2 xs={12} sx={{ mt: 2 }}>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Accordion key={`skeleton-accordion-${i}`} sx={{ mb: 2 }} expanded>
              <AccordionSummary>
                <Grid2
                  container
                  spacing={1}
                  alignItems="center"
                  mb={1}
                  sx={{ width: "100%" }}
                >
                  <Grid2 size={11}>
                    <Skeleton variant="text" width="30%" height={30} />
                  </Grid2>
                  <Grid2
                    size={1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      marginLeft: "-30px",
                    }}
                  >
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton variant="circular" width={30} height={30} />
                  </Grid2>
                </Grid2>
              </AccordionSummary>
              <AccordionDetails>
                {Array.from({ length: 2 }).map((_, j) => (
                  <Grid2
                    container
                    spacing={1}
                    alignItems="center"
                    key={`skeleton-item-${i}-${j}`}
                    mb={1}
                    sx={{ width: "100%" }}
                  >
                    <Grid2 size={11}>
                      <Skeleton variant="text" width="15%" height={30} />
                    </Grid2>
                    <Grid2
                      size={1}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                        marginLeft: "-30px",
                      }}
                    >
                      <Skeleton variant="circular" width={30} height={30} />
                      <Skeleton variant="circular" width={30} height={30} />
                    </Grid2>
                  </Grid2>
                ))}
              </AccordionDetails>
            </Accordion>
          ))
        ) :
          Object.keys(groupedData).length === 0 ? (
            <Accordion disabled expanded>
              <AccordionSummary>
                <Typography
                  sx={{
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  Record not found.
                </Typography>
              </AccordionSummary>
            </Accordion>
          ) : (
            Object.entries(groupedData).map(([category, items]) => {
              const isEditingCat = categoryEditMap[category]?.editing;
              const tempCat = categoryEditMap[category]?.temp || "";

              return (
                <Accordion
                  key={category}
                  sx={{ mb: 2 }}
                  expanded={expanded === category}
                  onChange={handleChange(category)}
                >
                  {/* <AccordionSummary expandIcon={<ExpandMoreIcon />}> */}
                  <AccordionSummary expandIcon={<ChevronDownIcon style={{width:"20px", height:"20px", fill:"#37352F"}} />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {isEditingCat ? (
                        <Grid2
                          container
                          alignItems="center"
                          sx={{ width: "100%" }}
                        >
                          <Grid2 size={{ xs: 12, md: 11 }}>
                            <TextField
                              size="small"
                              value={tempCat}
                              onChange={(e) =>
                                updateTempCategory(category, e.target.value)
                              }
                              sx={{ minWidth: "300px" }}
                            />
                          </Grid2>
                          <Grid2
                            size={{ xs: 12, md: 1 }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "20px",
                            }}
                          >
                            <IconButton
                              onClick={() =>
                                handleEditCategory(category, tempCat)
                              }
                              size="small"
                              color="success"
                            >
                               {/* <GiCheckMark /> */}
                              <CheckIcon style={{ fontSize: "30px", width:"30px", height:"30px", fill:"#32cd32" }} />
                            </IconButton>
                            <IconButton
                              onClick={() => cancelEditCategory(category)}
                              size="small"
                              color="error"
                            >
                              <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px" }} />
                            </IconButton>
                          </Grid2>
                        </Grid2>
                      ) : (
                        <Grid2
                          container
                          alignItems="center"
                          sx={{ width: "100%" }}
                        >
                          <Grid2 size={{ xs: 12, md: 11 }}>
                            <Typography>
                              <Typography
                                component="span"
                                sx={{ fontWeight: 600, display: "inline" }}
                              >
                                Category Name:{" "}
                              </Typography>
                              {category}
                            </Typography>
                          </Grid2>
                          <Grid2
                            size={{ xs: 12, md: 1 }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "20px",
                            }}
                          >
                            {editAccess &&
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => startEditCategory(category)}
                              >
                                 {/* <MdOutlineEdit /> */}
                                <EditIcon style={{ width: "20px", height: "20px", fill:"#32cd32" }} />
                              </IconButton>
                            }
                            {deleteAccess &&
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteCategory(items)}
                              >
                                {/* <MdDelete /> */}
                                 <DeleteIcon style={{width:"20px", height:"20px", fill:"#dc143c"}} />
                              </IconButton>
                            }
                          </Grid2>
                        </Grid2>
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {items.map((item) => {
                      const isEditing = editMap[item.id];
                      return (
                        <Grid2
                          container
                          spacing={1}
                          alignItems="center"
                          key={item.id}
                          mb={1}
                          sx={{ width: "100%" }}
                        >
                          <Grid2 size={{ xs: 8, md: 11 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                sx={{ minWidth: "300px" }}
                                value={editMap[item.id]?.value}
                                onChange={(e) =>
                                  handleEditChange(item.id, e.target.value)
                                }
                              />
                            ) : (
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 600, display: "inline" }}
                                >
                                  Value:{" "}
                                </Typography>
                                {item.value}
                              </Typography>
                            )}
                          </Grid2>
                          <Grid2
                            size={{ xs: 4, md: 1 }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: 'end',
                              gap: "20px",
                              // marginLeft: "-30px",
                            }}
                          >
                            {isEditing ? (
                              <>
                                <IconButton
                                  onClick={() => saveEdit(item.id)}
                                  size="small"
                                  color="success"
                                >
                                  {/* <GiCheckMark /> */}
                                   <CheckIcon style={{ fontSize: "30px", width:"30px", height:"30px", fill:"#32cd32" }} />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    setEditMap((prev) => {
                                      const copy = { ...prev };
                                      delete copy[item.id];
                                      return copy;
                                    })
                                  }
                                  size="small"
                                  color="error"
                                >
                                  {/* <IoMdClose /> */}
                                  <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px" }} />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton
                                  onClick={() => {
                                    resetAllEdits();
                                    setEditMap({
                                      [item.id]: {
                                        name: category,
                                        value: item.value,
                                      },
                                    });
                                  }}
                                  size="small"
                                  color="success"
                                >
                                   {/* <MdOutlineEdit /> */}
                                  <EditIcon style={{ width: "20px", height: "20px", fill:"#32cd32" }} />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDelete(item.id)}
                                  size="small"
                                  color="error"
                                >
                                  {/* <MdDelete /> */}
                                  <DeleteIcon style={{width:"20px", height:"20px", fill:"#dc143c"}} />
                                </IconButton>
                              </>
                            )}
                          </Grid2>
                        </Grid2>
                      );
                    })}
                    {addAccess &&
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Add value"
                          value={addRow[category] || ""}
                          onChange={(e) =>
                            setAddRow((prev) => ({
                              ...prev,
                              [category]: e.target.value,
                            }))
                          }
                          sx={{ minWidth: { xs: "200px", sm: '300px' }, mt:'15px' }}
                        />
                        <IconButton onClick={() => handleAddValue(category)}>
                           {/* <AddIcon /> */}
                          <AddNewIcon style={{ height: "20px", width: "20px", }}   />
                        </IconButton>
                      </Box>
                    }

                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
      </Grid2>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>
          {isCategoryDelete ? "Delete Category" : "Delete Setting"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isCategoryDelete
              ? "Are you sure you want to delete this category and all its values?This action cannot be undone."
              : "Are you sure you want to delete this setting? This action cannot be undone."}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AllSettings;
